'use client';

import { aggregationsAtom } from '@core/Atoms/Search/AggregationsAtom';
import { Locale } from '@core/Entities/Locale/Locale.entity';
import { AggregationsResults } from '@core/Entities/Search/Aggregations/Aggregations.entity';
import { useAtomValue } from 'jotai';

import {
  SectionConfig,
  createSection,
  ExtendedAggregations,
  getInterlinkingSections,
} from './MakeModelInterlinkingSections';

import styles from './MakeModelInterlinking.module.css';

type MakeModelInterlinkingClientProps = {
  make: string;
  modelWithMake: string | undefined;
  locale: Locale;
  messages: Record<string, string>;
  bodyTypeMessages: Record<string, string>;
  fuelTypeMessages: Record<string, string>;
  gearBoxMessages: Record<string, string>;
  colorMessages: Record<string, string>;
  serverAggregations: AggregationsResults['aggregations'];
  makeDisplayName: string;
  modelDisplayName: string | undefined;
};

const getMessageDictionary = (
  config: SectionConfig,
  bodyTypeMessages: Record<string, string>,
  fuelTypeMessages: Record<string, string>,
  gearBoxMessages: Record<string, string>,
  colorMessages: Record<string, string>,
): Record<string, string> => {
  switch (config.messageKey) {
    case 'body-types':
      return bodyTypeMessages;
    case 'fuel-types':
      return fuelTypeMessages;
    case 'transmissions':
      return gearBoxMessages;
    case 'colors':
      return colorMessages;
    default:
      return {};
  }
};

export const MakeModelInterlinkingClient = ({
  make,
  modelWithMake,
  locale,
  messages,
  bodyTypeMessages,
  fuelTypeMessages,
  gearBoxMessages,
  colorMessages,
  serverAggregations,
  makeDisplayName,
  modelDisplayName,
}: MakeModelInterlinkingClientProps) => {
  const atomAggregations = useAtomValue(aggregationsAtom);
  // Use the atom aggregations if available, otherwise fall back to the server aggregations
  const aggregations: ExtendedAggregations = atomAggregations['body-type']
    ? {
        ...atomAggregations,
        make: serverAggregations.make,
        model: serverAggregations.model,
        variant: serverAggregations.variant,
        // Ensure we have the variants array from atom aggregations
        variants: atomAggregations.variants,
      }
    : serverAggregations;

  const sections = getInterlinkingSections({ model: modelWithMake })
    .map(config => {
      const messageDict = getMessageDictionary(
        config,
        bodyTypeMessages,
        fuelTypeMessages,
        gearBoxMessages,
        colorMessages,
      );

      return createSection(
        config,
        aggregations,
        make,
        modelWithMake,
        locale,
        messages,
        messageDict,
        makeDisplayName,
        modelDisplayName,
      );
    })
    .filter(Boolean);

  if (sections.length === 0) {
    return null;
  }

  const padSections = 6 - sections.length;

  return (
    <div className={styles.sections}>
      {sections}
      {Array.from({ length: padSections }).map((_, index) => (
        <div key={index} className={styles.section} />
      ))}
    </div>
  );
};
